import { queryClient } from 'App';
import { getRecoil, setRecoil } from 'recoil-nexus';

import { iaRa } from 'lib/helpers/utility';
import { resolvePendingExportTab } from 'lib/helpers/pdf/download/lib/pendingExportTabs';

import { openDock } from 'components/Dock/lib/useDock';
import { DOCK_KINDS } from 'components/Dock/lib/dockKinds';

import { exportProgressState } from './exportProgressState';
import { EXPORT_JOBS_QUERY_KEY, EXPORT_STATUS } from './constants';
import { advanceStatus, isTerminalStatus, rankStatus } from './helpers';

// Single write path for the export dock, shared by the socket listener and the
// enqueue helper. Keeping the merge here (next to the atom) means socket-side changes
// don't leak into unrelated callers, and the layering stays lib → lib.
export const upsertExportJob = (data) => {
  const {
    exportJobId,
    status,
    type,
    fileName,
    downloadUrl,
    expiresAt,
    files,
    error,
    stale,
    staleSince
  } = data || {};
  if (!exportJobId) return;
  // Ignore malformed events with no/unknown status — they'd otherwise store an
  // undefined status and open the dock on garbage.
  if (rankStatus(status) < 0) return;

  const prev = getRecoil(exportProgressState);
  // A dismissed job keeps ticking on the socket; don't resurrect it.
  if (iaRa(prev.dismissed).includes(exportJobId)) return;

  const prevJob = prev.jobs?.[exportJobId] || {};
  const wasTerminal = isTerminalStatus(prevJob.status);
  const nextStatus = advanceStatus(prevJob.status, status);

  setRecoil(exportProgressState, {
    ...prev,
    jobs: {
      ...prev.jobs,
      [exportJobId]: {
        exportJobId,
        status: nextStatus,
        type: type ?? prevJob.type,
        fileName: fileName ?? prevJob.fileName,
        downloadUrl: downloadUrl ?? prevJob.downloadUrl,
        expiresAt: expiresAt ?? prevJob.expiresAt,
        files: files ?? prevJob.files,
        error: nextStatus === EXPORT_STATUS.COMPLETED ? undefined : (error ?? prevJob.error),
        stale: nextStatus === EXPORT_STATUS.COMPLETED ? false : (stale ?? prevJob.stale),
        staleSince:
          nextStatus === EXPORT_STATUS.COMPLETED ? undefined : (staleSince ?? prevJob.staleSince)
      }
    }
  });

  // A tab is only parked for a job the user asked to view in the browser; handing it
  // the file there keeps that promise without a second, popup-blocked window.open.
  resolvePendingExportTab({
    exportJobId,
    status: nextStatus,
    downloadUrl: downloadUrl ?? prevJob.downloadUrl
  });

  openDock({ id: 'export', kind: DOCK_KINDS.EXPORT, side: 'right' });

  if (isTerminalStatus(nextStatus) && !wasTerminal) {
    queryClient.invalidateQueries([EXPORT_JOBS_QUERY_KEY]);
  }
};

// Seed a freshly enqueued job into the dock (used by the enqueue helpers).
export const seedQueuedExport = (exportJobId, type) =>
  upsertExportJob({ exportJobId, status: EXPORT_STATUS.QUEUED, type });
